import { AxiosProgressEvent } from 'axios';

import { IReportCommunityCommentOrPostRequest } from '../types/commentTypes';
import { ICommunityPollVotesResult } from '../types/communityMessagesTypes';
import {
  IChannelDetails,
  IChannelGroup,
  IChannelPostDetails,
  IComment,
  ICommentReqBody,
  IEditPostReqBody,
  IFileUploadResponse,
  ILink,
  IPostReqBody,
} from '../types/communityTypes';
import { EUploadModuleType } from '../types/fileTypes';
import API from './';

export default abstract class CommunityAPI {
  static root = 'api/v1/community';

  static channel = '/channels';

  static getAllChannelGroups = () =>
    API.get<IChannelGroup[]>(`${this.root}${this.channel}/groups`);

  static getAllCommunityLinks = () => API.get<ILink[]>(`${this.root}/links`);

  static getChannelDetailsById = (channelId: string) =>
    API.get<IChannelDetails>(`${this.root}${this.channel}/${channelId}`);

  static getPostsByChannelId = (channelId: string, queryParams: string) =>
    API.get<{
      posts: IChannelPostDetails[];
      totalPosts: number;
      lastPostId: string;
    }>(`${this.root}${this.channel}/${channelId}/posts?${queryParams}`);

  static getPostsByUserId = (
    userId: string,
    queryParams: string,
    channels: string[],
  ) =>
    API.post<{
      posts: IChannelPostDetails[];
      totalPosts: number;
      lastPostId: string;
    }>(`${this.root}${this.channel}/posts/user/${userId}?${queryParams}`, {
      channels,
    });

  static createPost = (data: IPostReqBody) => {
    return API.post<IChannelPostDetails>(
      `${this.root}${this.channel}/posts`,
      data,
    );
  };

  static editPost = (
    channelId: string,
    postId: string,
    data: IEditPostReqBody,
  ) =>
    API.put<IChannelPostDetails[]>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}`,
      data,
    );

  static increasePostViews = (channelId: string, postId: string) =>
    API.post<any>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/views`,
    );

  static getCommunityPostById = (channelId: string, postId: string) =>
    API.get(`${this.root}${this.channel}/${channelId}/posts/${postId}`);

  static addVoteOnCommunityPoll = (
    channelId: string,
    postId: string,
    data: { option: string },
  ) =>
    API.post(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/votes`,
      data,
    );

  static removeVoteOnCommunityPoll = (
    channelId: string,
    postId: string,
    data: { option: string },
  ) =>
    API.delete(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/votes`,
      { data },
    );

  static addLikeToCommunityPost = (channelId: string, postId: string) =>
    API.post(`${this.root}${this.channel}/${channelId}/posts/${postId}/likes`);

  static removeLikeToCommunityPost = (channelId: string, postId: string) =>
    API.delete(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/likes`,
    );

  static updatePost = (channelId: string, postId: string, data: any) =>
    API.put(`${this.root}${this.channel}/${channelId}/posts/${postId}`, data);

  static addPinnedToCommunityPost = (channelId: string, postId: string) =>
    API.post(`${this.root}${this.channel}/${channelId}/posts/${postId}/pin`);

  static removePinnedFromCommunityPost = (channelId: string, postId: string) =>
    API.delete(`${this.root}${this.channel}/${channelId}/posts/${postId}/pin`);

  static removeCommunityPost = (channelId: string, postId: string) =>
    API.delete(`${this.root}${this.channel}/${channelId}/posts/${postId}`);

  // ---------Comments
  static crateComment = (
    channelId: string,
    postId: string,
    data: ICommentReqBody,
  ) =>
    API.post<IComment>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments`,
      data,
    );

  static getComments = (
    channelId: string,
    postId: string,
    query: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _: string,
  ) =>
    API.get<{ comments: IComment; totalComments: 0 }>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments?${query}`,
    );

  static getReplyComments = (
    channelId: string,
    postId: string,
    query: string,
    commentId: string,
  ) =>
    API.get<{ comments: IComment; totalComments: 0 }>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments/${commentId}?${query}`,
    );

  static addCommentLike = (
    channelId: string,
    postId: string,
    commentId: string,
  ) =>
    API.post<IComment>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments/${commentId}/likes`,
    );

  static removeCommentLike = (
    channelId: string,
    postId: string,
    commentId: string,
  ) =>
    API.delete<IComment>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments/${commentId}/likes`,
    );

  static removeComment = (
    channelId: string,
    postId: string,
    commentId: string,
  ) =>
    API.delete<IComment>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments/${commentId}`,
    );

  static updateComment = (
    channelId: string,
    postId: string,
    commentId: string,
    data: ICommentReqBody,
  ) =>
    API.put<IComment>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/comments/${commentId}`,
      data,
    );

  // ---------Comments

  static getPollVotesInPost = (
    channelId: string,
    postId: string,
    query: string,
  ) =>
    API.get<ICommunityPollVotesResult>(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/votes?${query}`,
    );

  static uploadCommunityMedia = (
    files: File[],
    onUploadProgress: (e: AxiosProgressEvent) => void,
    uploadKey: EUploadModuleType,
  ) => {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    return API.post<IFileUploadResponse>(
      `${this.root?.replace('/community', '')}/tm-cloud/upload/general/${uploadKey}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      },
    );

    // return API.post<ICommunityUploadedMedia[]>(
    //   `${this.root}/uploads/assets`,
    //   formData,
    //   {
    //     headers: {
    //       'Content-Type': 'multipart/form-data',
    //     },
    //     onUploadProgress,
    //   },
    // );
  };

  // ----------------User Settings
  static updateUserSettings = (channelId: string, data: any) =>
    API.patch(`${this.root}${this.channel}/${channelId}/user-settings`, data);

  // ----------------User Settings

  static reportCommentOrPost = (data: IReportCommunityCommentOrPostRequest) => {
    return API.post(`${this.root}/report`, data);
  };

  static getLikedByUsers = (channelId: string, postId: string, query: string) =>
    API.get(
      `${this.root}${this.channel}/${channelId}/posts/${postId}/likes?${query}`,
    );

  static getMetaData = (channelIds: string[]) =>
    API.post(`${this.root}${this.channel}/metadata`, { channels: channelIds });

  static visitCommunityChannel = (channelId: string) =>
    API.post<any>(`${this.root}${this.channel}/${channelId}/visit`);

  static channelVideoPostView = ({
    channelPost,
    mediaContent,
  }: {
    channelPost: string;
    mediaContent: string;
  }) =>
    API.post<any>(`${this.root}${this.channel}/posts/media/view`, {
      channelPost,
      mediaContent,
    });
}
